import { Component } from '@angular/core';

import { of } from 'rxjs';

import {
  AiToolsComponent,
  AttachmentsCardsComponent,
  CamCommunicationsService,
  CamCommunicationsTemplatesService,
  CommunicationTemplateEditComponent,
  CommunicationTemplateListComponent,
  ConversationAttachments,
  ConversationAttachmentsComponent,
  ConversationComponent,
  ListComponent,
  MessageBoxContainerComponent,
  MessageBoxInputComponent,
  MessageBoxToolbarComponent,
  MessageChatComponent,
  MessageContainerComponent,
  MessageMailComponent,
  MessageSystemComponent,
  LightComponent,
  ParticipantComponent,
  ParticipantsListComponent,
  VariantEditComponent,
} from '@camelot/core';
import { CamContactService, CamDocumentsService } from '@camelot/services';
import { CamUsersService } from '@camelot/user';

import { TestIdDirective } from '@camelot/testing-harness';

const emptyHandle = { get: () => [], get$: () => of([]) };

const communicationsServiceMock = {
  communicationByTaskId: emptyHandle,
  createConversation: () => of(null),
  createConversation$: () => of(null),
  documentsIdsByTaskId: emptyHandle,
  fetchConversationByTaskId$: () => of(null),
  fetchMySignature: () => of(null),
  fetchMySignature$: () => of(null),
  fetchResponseToCommunication$: () => of(null),
  mySignature: { get: () => null, get$: () => of(null) },
  watchConversationByTaskId$: () => of(null),
};

const templatesServiceMock = {
  communicationTemplate: { get: () => null, get$: () => of(null) },
  communicationTemplates: emptyHandle,
  createTemplate: () => of(null),
  createTemplate$: () => of(null),
  createVariants: () => of(null),
  createVariants$: () => of(null),
  fetchConversationTemplate: () => of(null),
  fetchConversationTemplate$: () => of(null),
  fetchConversationTemplateKeys: () => of([]),
  fetchConversationTemplateKeys$: () => of([]),
  fetchConversationTemplates: () => of([]),
  fetchConversationTemplates$: () => of([]),
  templateKeys: emptyHandle,
  updateTemplate: () => of(null),
  updateTemplate$: () => of(null),
  updateVariants: () => of(null),
  updateVariants$: () => of(null),
};

const usersServiceMock = {
  currentUser: { get: () => null, get$: () => of(null) },
  fetchCurrentUser$: () => of(null),
  fetchUsers: () => of([]),
  fetchUsers$: () => of([]),
  fetchUsersCustomers: () => of([]),
  fetchUsersCustomers$: () => of([]),
  users: emptyHandle,
};

const contactServiceMock = {
  fetchSearchContact$: () => of([]),
};

const documentsServiceMock = {
  documents: emptyHandle,
  fetchDocuments$: () => of([]),
  getDocuments$: () => of([]),
};

const communicationMock = {
  content: 'hello',
  createdDate: '2026-01-01T10:00:00Z',
  id: 'comm-1',
  participants: [],
  status: 1,
  type: 1,
} as never;

@Component({
    imports: [
        AiToolsComponent,
        AttachmentsCardsComponent,
        CommunicationTemplateEditComponent,
        CommunicationTemplateListComponent,
        ConversationAttachmentsComponent,
        ConversationComponent,
        LightComponent,
        ListComponent,
        MessageBoxContainerComponent,
        MessageBoxInputComponent,
        MessageBoxToolbarComponent,
        MessageChatComponent,
        MessageContainerComponent,
        MessageMailComponent,
        MessageSystemComponent,
        ParticipantComponent,
        ParticipantsListComponent,
        TestIdDirective,
        VariantEditComponent,
    ],
    providers: [
        { provide: CamCommunicationsService, useValue: communicationsServiceMock },
        { provide: CamCommunicationsTemplatesService, useValue: templatesServiceMock },
        { provide: CamUsersService, useValue: usersServiceMock },
        { provide: CamContactService, useValue: contactServiceMock },
        { provide: CamDocumentsService, useValue: documentsServiceMock },
    ],
    template: `
    <cam-conversation
      camTestId="cam-conversation"
      taskId="task-1"
      [contactId$]="this.contactId$"
      [statusTemplate]="statusTemplate"
    />
    <cam-conversations-ai-tools camTestId="cam-conversations-ai-tools" taskId="task-1" />
    <cam-communication-attachments-cards
      camTestId="cam-communication-attachments-cards"
      [documentsIds]="this.documentsIds"
    />
    <cam-communication-conversation-attachments
      camTestId="cam-communication-conversation-attachments"
      [attachments]="this.attachments"
    />
    <cam-message-list
      camTestId="cam-message-list"
      [conversationParticipants]="this.participants"
      [list]="this.emptyList"
      taskId="task-1"
    />
    <cam-message-box-toolbar camTestId="cam-message-box-toolbar" [attachments]="this.attachments" taskId="task-1" />
    <cam-communication-message-box-container
      camTestId="cam-communication-message-box-container"
      [conversationParticipants]="this.participants"
      [statusTemplate]="statusTemplate"
      taskId="task-1"
    />
    <cam-message-box-input
      camTestId="cam-message-box-input"
      [activeTab]="1"
      [conversationParticipants]="this.participants"
    />
    <cam-communication-message-container
      camTestId="cam-communication-message-container"
      [conversationParticipants]="this.participants"
      [communication]="this.communication"
      taskId="task-1"
    />
    <cam-communication-message-chat
      camTestId="cam-communication-message-chat"
      [conversationParticipants]="this.participants"
      [communication]="this.communication"
    />
    <cam-communication-message-mail
      camTestId="cam-communication-message-mail"
      [conversationParticipants]="this.participants"
      [communication]="this.communication"
      taskId="task-1"
    />
    <cam-communication-message-system
      camTestId="cam-communication-message-system"
      [conversationParticipants]="this.participants"
      [communication]="this.communication"
    />
    <cam-communication-participant-light
      camTestId="cam-communication-participant-light"
      [conversationParticipants]="this.participants"
      [participants]="this.participants"
    />
    <cam-communication-participant
      camTestId="cam-communication-participant"
      [conversationParticipants]="this.participants"
      [participant]="this.participant"
    />
    <cam-communication-participants-list
      camTestId="cam-communication-participants-list"
      [conversationParticipants]="this.participants"
    />
    <cam-communication-template-edit camTestId="cam-communication-template-edit" />
    <cam-communication-template-variant-edit camTestId="cam-communication-template-variant-edit" id="template-1" />
    <cam-communication-template-list camTestId="cam-communication-template-list" />
    <ng-template #statusTemplate>
      <div>status</div>
    </ng-template>
  `
})
export class CoreCommunicationMessagesCase {
  public attachments: ConversationAttachments = {
    documentsIds: { communication: [], project: [], task: [] },
    preSet: [],
    value: [],
  };
  public communication = communicationMock;
  public contactId$ = of('contact-1');
  public documentsIds = { communication: [], invoice: [], project: [], task: [] };
  public emptyList: never[] = [];
  public participant = { id: 'participant-1', userId: 'user-1' } as never;
  public participants: never[] = [];
}
